/**
 * HeroCinematic - hero full-bleed CWV-safe (spec sec 7).
 * Contract:
 *   1. Poster <img> render ngay trong SSR (LCP = poster, fetchpriority=high).
 *   2. <video> KHONG co trong SSR markup - inject CLIENT-ONLY sau window load,
 *      chi khi: co videoSources + !prefers-reduced-motion + !saveData.
 *   3. prefers-reduced-motion -> khong bao gio load video (CSS + JS guard).
 *   4. Phase 1A: khong asset -> flat ink bg + scrim + content (van hop le).
 * Video budget: <= 2.5MB, 6-8s loop, codec ladder AV1 -> H265 -> H264 (spec sec 7).
 *
 * fetchPriority: React 19 supports the camelCase `fetchPriority` prop natively
 * (typechecks) and emits lowercase `fetchpriority="high"` in HTML output - the
 * LCP browser hint. No loose cast / @ts-expect-error needed.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

export type HeroVideoSource = { src: string; type: string };

type Props = {
  posterSrc?: string;
  posterAlt?: string;
  videoSources?: HeroVideoSource[];
  children: ReactNode;
};

function canAutoplayVideo(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const conn = (navigator as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (conn?.saveData) return false;
  if (conn?.effectiveType && /(^|\b)(slow-2g|2g|3g)\b/.test(conn.effectiveType)) return false;
  return true;
}

export function HeroCinematic({ posterSrc, posterAlt = '', videoSources, children }: Props) {
  const [videoOn, setVideoOn] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!videoSources || videoSources.length === 0) return;
    if (!canAutoplayVideo()) return;
    let cancelled = false;
    const arm = () => { if (!cancelled) setVideoOn(true); };
    if (document.readyState === 'complete') {
      const idle = (window as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
      if (idle) idle(arm); else setTimeout(arm, 1200);
    } else {
      window.addEventListener('load', arm, { once: true });
    }
    return () => { cancelled = true; window.removeEventListener('load', arm); };
  }, [videoSources]);

  useEffect(() => {
    if (videoOn && videoRef.current) {
      videoRef.current.play().catch(() => { /* autoplay denied -> poster van hien */ });
    }
  }, [videoOn]);

  return (
    <section className="ed-hero" aria-labelledby="hero-heading">
      {posterSrc ? (
        <img
          className="ed-hero__poster"
          src={posterSrc}
          alt={posterAlt}
          decoding="async"
          fetchPriority="high"
        />
      ) : null}
      {videoOn && videoSources ? (
        <video
          ref={videoRef}
          className="ed-hero__video ed-hero__video--ready"
          muted
          loop
          playsInline
          preload="none"
          poster={posterSrc}
          aria-hidden="true"
          tabIndex={-1}
        >
          {videoSources.map((s) => (<source key={s.src} src={s.src} type={s.type} />))}
        </video>
      ) : null}
      <div className="ed-hero__scrim" aria-hidden="true" />
      <div className="ed-hero__content">{children}</div>
    </section>
  );
}
